import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { ArrowUpRight, Megaphone, Play } from "lucide-react";
import { LINKS } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const mediaUrl = (item) => `${process.env.REACT_APP_BACKEND_URL}/api/media/${item.media_path}`;

const Media = ({ item, className }) =>
  item.media_type === "video" ? (
    <video src={mediaUrl(item)} controls playsInline preload="metadata" className={className} />
  ) : (
    <img src={mediaUrl(item)} alt="JGW update" loading="lazy" className={className} />
  );

const VideoBadge = () => (
  <span className="absolute left-3 top-3 z-10 inline-flex items-center gap-1.5 rounded-md bg-black/75 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-white backdrop-blur">
    <Play size={11} strokeWidth={2.5} />
    Video
  </span>
);

const SponsoredBadge = () => (
  <span
    data-testid="sponsored-badge"
    className="inline-flex items-center gap-1.5 rounded-md border border-white/20 bg-white/5 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-silver-light"
  >
    <Megaphone size={11} strokeWidth={2.2} />
    Sponsored
  </span>
);

const NewsFeed = () => {
  const [posts, setPosts] = useState(null);
  const [ads, setAds] = useState({});

  const load = useCallback(async () => {
    try {
      const [{ data: postsData }, { data: adsData }] = await Promise.all([
        axios.get(`${API}/posts`),
        axios.get(`${API}/ads`),
      ]);
      setPosts(postsData);
      setAds(adsData || {});
    } catch {
      setPosts((p) => p ?? []);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const featured = posts?.[0];
  const bannerAd = ads.banner || null;
  const feedAd = ads.feed || null;

  let bannerPost = null;
  let gridPosts = posts ? posts.slice(1) : [];
  if (!bannerAd && gridPosts.length > 0) {
    bannerPost = gridPosts[0];
    gridPosts = gridPosts.slice(1);
  }
  const gridItems = [...gridPosts];
  if (feedAd) {
    gridItems.splice(Math.min(2, gridItems.length), 0, { __ad: true, ...feedAd });
  }

  return (
    <section id="latest" data-testid="news-feed-section" className="border-t border-white/5 bg-ink py-10 lg:py-12">
      <div className="mx-auto max-w-7xl px-5 lg:px-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="flex items-center gap-4">
            <span className="inline-flex items-center gap-2 rounded-full border border-whatsapp/30 bg-whatsapp/10 px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.25em] text-whatsapp">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-whatsapp opacity-70" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-whatsapp" />
              </span>
              Live
            </span>
            <h2 className="font-display text-3xl font-bold tracking-tight text-white md:text-4xl">
              Latest Updates
            </h2>
          </div>
          <p className="text-xs font-medium text-silver-dark">Posted throughout the day</p>
        </div>

        {posts === null ? (
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="animate-pulse rounded-2xl border border-white/10 bg-white/[0.04]">
                <div className="aspect-[4/3] rounded-t-2xl bg-white/5" />
                <div className="space-y-3 p-5">
                  <div className="h-4 w-full rounded bg-white/10" />
                  <div className="h-4 w-2/3 rounded bg-white/10" />
                </div>
              </div>
            ))}
          </div>
        ) : posts.length === 0 && !bannerAd && !feedAd ? (
          <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.04] p-10 text-center">
            <p className="font-display text-xl font-bold text-white">New updates are on the way.</p>
            <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-silver">
              JGW posts updates throughout the day. Join the WhatsApp community to get
              every update the moment it is posted.
            </p>
            <a
              href={LINKS.community}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="feed-empty-join-button"
              className="mt-6 inline-flex items-center justify-center gap-2 rounded-xl bg-whatsapp px-6 py-3.5 text-sm font-bold text-white transition-colors duration-200 hover:bg-whatsapp-dark"
            >
              Join Our Community
            </a>
          </div>
        ) : (
          <>
            {featured && (
              <article
                data-testid={`post-featured-${featured.id}`}
                className="mt-8 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04] lg:grid lg:grid-cols-2"
              >
                <div className="relative bg-black">
                  {featured.media_type === "video" && <VideoBadge />}
                  <Media item={featured} className="aspect-video w-full object-cover lg:h-full lg:min-h-[300px]" />
                </div>
                <div className="flex flex-col justify-center p-6 sm:p-8">
                  <span className="w-fit rounded-md bg-whatsapp px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-black">
                    Latest
                  </span>
                  <p className="mt-4 text-lg font-semibold leading-relaxed text-white md:text-xl">
                    {featured.description}
                  </p>
                  {featured.link && (
                    <a
                      href={featured.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`post-link-${featured.id}`}
                      className="mt-6 inline-flex w-fit items-center gap-2 rounded-xl border border-whatsapp/50 px-5 py-3 text-sm font-bold text-whatsapp transition-colors duration-200 hover:bg-whatsapp hover:text-black"
                    >
                      Open Link
                      <ArrowUpRight size={15} strokeWidth={2.2} />
                    </a>
                  )}
                </div>
              </article>
            )}

            {bannerAd && (
              <aside
                data-testid={`ad-banner-${bannerAd.id}`}
                className="mt-5 overflow-hidden rounded-2xl border border-white/15 bg-charcoal-800 lg:grid lg:grid-cols-2"
              >
                <div className="relative bg-black">
                  {bannerAd.media_type === "video" && <VideoBadge />}
                  <Media item={bannerAd} className="aspect-video w-full object-cover lg:h-full lg:min-h-[240px]" />
                </div>
                <div className="flex flex-col justify-center p-6 sm:p-8">
                  <SponsoredBadge />
                  {bannerAd.description && (
                    <p className="mt-4 text-base font-semibold leading-relaxed text-white md:text-lg">
                      {bannerAd.description}
                    </p>
                  )}
                  {bannerAd.link && (
                    <a
                      href={bannerAd.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`ad-link-${bannerAd.id}`}
                      className="mt-6 inline-flex w-fit items-center gap-2 rounded-xl bg-white px-5 py-3 text-sm font-bold text-black transition-colors duration-200 hover:bg-whatsapp"
                    >
                      Learn More
                      <ArrowUpRight size={15} strokeWidth={2.2} />
                    </a>
                  )}
                </div>
              </aside>
            )}

            {bannerPost && (
              <article
                data-testid={`post-banner-${bannerPost.id}`}
                className="mt-5 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04] lg:grid lg:grid-cols-2"
              >
                <div className="relative bg-black">
                  {bannerPost.media_type === "video" && <VideoBadge />}
                  <Media item={bannerPost} className="aspect-video w-full object-cover lg:h-full lg:min-h-[240px]" />
                </div>
                <div className="flex flex-col justify-center p-6 sm:p-8">
                  <p className="text-base font-semibold leading-relaxed text-white md:text-lg">
                    {bannerPost.description}
                  </p>
                  {bannerPost.link && (
                    <a
                      href={bannerPost.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`post-link-${bannerPost.id}`}
                      className="mt-6 inline-flex w-fit items-center gap-2 rounded-xl border border-whatsapp/50 px-5 py-3 text-sm font-bold text-whatsapp transition-colors duration-200 hover:bg-whatsapp hover:text-black"
                    >
                      Open Link
                      <ArrowUpRight size={15} strokeWidth={2.2} />
                    </a>
                  )}
                </div>
              </article>
            )}

            {gridItems.length > 0 && (
              <div className="mt-5 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {gridItems.map((item) =>
                  item.__ad ? (
                    <aside
                      key={`ad-${item.id}`}
                      data-testid={`ad-card-${item.id}`}
                      className="group overflow-hidden rounded-2xl border border-white/15 bg-charcoal-800"
                    >
                      <div className="relative overflow-hidden bg-black">
                        <span className="absolute left-3 top-3 z-10">
                          <SponsoredBadge />
                        </span>
                        <Media
                          item={item}
                          className="aspect-[4/3] w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                        />
                      </div>
                      {(item.description || item.link) && (
                        <div className="p-5">
                          {item.description && (
                            <p className="line-clamp-4 text-[15px] font-medium leading-relaxed text-silver-light">
                              {item.description}
                            </p>
                          )}
                          {item.link && (
                            <a
                              href={item.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid={`ad-link-${item.id}`}
                              className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-white transition-colors duration-200 hover:text-whatsapp"
                            >
                              Learn More
                              <ArrowUpRight size={14} strokeWidth={2.2} />
                            </a>
                          )}
                        </div>
                      )}
                    </aside>
                  ) : (
                    <article
                      key={item.id}
                      data-testid={`post-card-${item.id}`}
                      className="group overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04] transition-colors duration-300 hover:border-white/25"
                    >
                      <div className="relative overflow-hidden bg-black">
                        {item.media_type === "video" && <VideoBadge />}
                        <Media
                          item={item}
                          className="aspect-[4/3] w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                        />
                      </div>
                      <div className="p-5">
                        <p className="line-clamp-4 text-[15px] font-medium leading-relaxed text-silver-light">
                          {item.description}
                        </p>
                        {item.link && (
                          <a
                            href={item.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`post-link-${item.id}`}
                            className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-whatsapp transition-colors duration-200 hover:text-white"
                          >
                            Open Link
                            <ArrowUpRight size={14} strokeWidth={2.2} />
                          </a>
                        )}
                      </div>
                    </article>
                  ),
                )}
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default NewsFeed;
